/**
 * @module root/shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want
// Core Libraries
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

// Services
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';

// Constants
import { API_MAP } from '@core/api/proxies/common-api-endpoints';

// Models
import { SearchSuggestionModel } from '@root/shared/models/search-suggestion.model';
import { map } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
/**
 * Service for handling search functionality in navbar
 */
@Injectable()
export class SearchService {
  public searchEvent: Subject<string>;

  constructor(
    private stateStoreUtilsService: StateStoreUtilsService,
    private apiGateway: ApiGatewayService,
    private objectService: ObjectService
  ) {
    this.searchEvent = new Subject();
  }

  /**
   * Function checks if search term is zip code, if length of input is 5 and it is numeric
   * @param searchTerm Search Term
   */
  public isZipCodeSearch(searchTerm: string): boolean {
    const zipCodeLength = 5;
    if (searchTerm.length !== zipCodeLength) {
      return false;
    } else {
      return /^\d+$/.test(searchTerm);
    }
  }

  /**
   * Function makes API call for search suggestions. It also accumulates similar search results from previous
   * searches and returns model for displaying data
   * @param searchTerm Search Term
   */
  public initiateSearchSuggestions(newSearch: string): Observable<SearchSuggestionModel> {
    const previousSearches = this.stateStoreUtilsService.getPreviousGlobalSearches();
    // Check for matching pervious searches
    const filteredPerviousSearchResults = previousSearches.filter((searchTerm: string) => {
      return searchTerm.indexOf(newSearch) >= -1;
    });
    // Make API call for suggestions and create model for search suggestions
    return this.getSearchSuggestions(newSearch).pipe(
      map((searchSuggestions: { 'auto-suggestions': string[] }) => {
        return new SearchSuggestionModel(
          newSearch,
          filteredPerviousSearchResults,
          this.objectService.get(searchSuggestions, 'auto-suggestions')
        );
      })
    );
  }

  /**
   * Function makes API call for Auto Suggestion.
   * @param searchTerm Search Term
   */
  public getSearchSuggestions(newSearch: string): Observable<{ 'auto-suggestions': string[] }> {
    const MAX_TOTAL_SUGGESTIONS = '6';
    const payload = {
      query: newSearch,
      limit: MAX_TOTAL_SUGGESTIONS,
    };
    return this.apiGateway.postRequest(API_MAP.API_AutoSuggestion, payload, null, null, null).pipe(
      map((response: HttpResponse<{ 'auto-suggestions': string[] }>) => {
        return response.body;
      })
    );
  }

  /**
   * Function caches latest search. We can only cache certain number of latest searches, funciton updates
   * cached searches based on criteria
   * @param searchTerm Term being searched
   */
  public cacheNewSearchTerm(searchTerm: string): void {
    const perviousSearches = this.stateStoreUtilsService.getPreviousGlobalSearches();
    if (perviousSearches.indexOf(searchTerm) < 0) {
      // Searching this for first time so add to begining of array
      perviousSearches.unshift(searchTerm);
    } else {
      // Need to move the search to the first location in the cached results
      perviousSearches.splice(perviousSearches.indexOf(searchTerm), 1);
      perviousSearches.unshift(searchTerm);
    }
    this.stateStoreUtilsService.setPreviousGlobalSearches(perviousSearches);
  }

  /*
   * Function use to trigger event for search like autocomplete and button click
   * @param value hold the value of autocomplete else no-result
   */
  public searchEventTrigger(value: string): void {
    this.searchEvent.next(value);
  }

  /**
   * This function used to listen changes of search box and get value.
   * cached searches based on criteria
   * @param searchTerm Term being searched
   */
  public getSearchEvent(): Observable<string> {
    return this.searchEvent.asObservable();
  }
}
